<script setup>
import { ref, computed, toRef } from 'vue';
import { toast } from 'dashboard/composables/useToast';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSelect']);

const { t } = useI18n();
const store = useStore();
const query = ref('');
const isRefreshing = ref(false);

const whatsAppTemplateMessages = useFunctionGetter(
  'inboxes/getFilteredWhatsAppTemplates',
  toRef(props, 'inboxId')
);

const filteredTemplateMessages = computed(() =>
  whatsAppTemplateMessages.value.filter(template =>
    template.name.toLowerCase().includes(query.value.toLowerCase())
  )
);

const getTemplateBody = template => {
  return findComponentByType(template, COMPONENT_TYPES.BODY)?.text || '';
};

const getTemplateHeader = template => {
  return findComponentByType(template, COMPONENT_TYPES.HEADER);
};

const getTemplateFooter = template => {
  return findComponentByType(template, COMPONENT_TYPES.FOOTER);
};

const getTemplateButtons = template => {
  return findComponentByType(template, COMPONENT_TYPES.BUTTONS);
};

const hasMediaContent = template => {
  const header = getTemplateHeader(template);
  return header && MEDIA_FORMATS.includes(header.format);
};

const mediaIcon = template => {
  const format = getTemplateHeader(template)?.format;
  if (format === 'VIDEO') return 'i-lucide-video';
  if (format === 'DOCUMENT') return 'i-lucide-file-text';
  return 'i-lucide-image';
};

const buttonIcon = button => {
  if (button.type === 'URL') return 'i-lucide-external-link';
  if (button.type === 'PHONE_NUMBER') return 'i-lucide-phone';
  return 'i-lucide-reply';
};

const escapeHtml = str =>
  String(str).replace(
    /[&<>"']/g,
    char =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char]
  );

// Renderiza o texto do template no estilo WhatsApp (negrito/italico/etc + variaveis).
const renderWhatsappText = text => {
  if (!text) return '';
  let html = escapeHtml(text);
  html = html.replace(/\*(.+?)\*/g, '<strong>$1</strong>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');
  html = html.replace(/~(.+?)~/g, '<del>$1</del>');
  html = html.replace(/```(.+?)```/g, '<code>$1</code>');
  html = html.replace(
    /\{\{(\d+)\}\}/g,
    '<span class="text-[#00a5f4] font-medium">{{$1}}</span>'
  );
  return html.replace(/\n/g, '<br>');
};

const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    toast.success(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    toast.error(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex gap-2 mb-2.5">
      <div
        class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
      >
        <fluent-icon icon="search" class="text-n-slate-12" size="16" />
        <input
          v-model="query"
          type="search"
          :placeholder="t('WHATSAPP_TEMPLATES.PICKER.SEARCH_PLACEHOLDER')"
          class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
        />
      </div>
      <button
        :disabled="isRefreshing"
        class="flex justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 disabled:opacity-50 disabled:cursor-not-allowed"
        :title="t('WHATSAPP_TEMPLATES.PICKER.REFRESH_BUTTON')"
        @click="refreshTemplates"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="text-n-slate-12 size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
      </button>
    </div>
    <div
      class="bg-n-background outline-n-container outline outline-1 rounded-lg max-h-[18.75rem] overflow-y-auto p-2.5"
    >
      <button
        v-for="template in filteredTemplateMessages"
        :key="template.id"
        class="block p-2 mb-2.5 w-full text-left rounded-lg cursor-pointer last:mb-0 hover:bg-n-alpha-2 dark:hover:bg-n-solid-2"
        @click="emit('onSelect', template)"
      >
        <!-- Nome do template + idioma -->
        <div class="flex gap-2 justify-between items-center mb-1.5">
          <p class="text-sm font-medium truncate text-n-slate-12">
            {{ template.name }}
          </p>
          <span
            class="inline-block px-2 py-0.5 text-xs leading-none rounded-lg shrink-0 bg-n-slate-3 text-n-slate-11"
          >
            {{ template.language }}
          </span>
        </div>
        <!-- Preview estilo WhatsApp -->
        <div class="p-2.5 rounded-lg bg-[#e5ddd5] dark:bg-[#0b141a]">
          <div
            class="ltr:ml-auto rtl:mr-auto max-w-[88%] rounded-lg ltr:rounded-tr-none rtl:rounded-tl-none bg-[#d9fdd3] dark:bg-[#005c4b] px-2.5 py-2 shadow-sm"
          >
            <!-- Header de midia -->
            <div
              v-if="hasMediaContent(template)"
              class="flex justify-center items-center mb-2 h-20 rounded bg-black/5 dark:bg-white/10 text-n-slate-11"
            >
              <span :class="mediaIcon(template)" class="size-7" />
            </div>
            <!-- Header de texto -->
            <p
              v-else-if="getTemplateHeader(template)?.format === 'TEXT'"
              class="mb-1 text-sm font-semibold text-n-slate-12"
              v-html="renderWhatsappText(getTemplateHeader(template).text)"
            />
            <!-- Corpo -->
            <p
              class="text-sm whitespace-pre-wrap text-n-slate-12"
              v-html="renderWhatsappText(getTemplateBody(template))"
            />
            <!-- Footer -->
            <p
              v-if="getTemplateFooter(template)"
              class="mt-1.5 text-xs text-n-slate-10"
            >
              {{ getTemplateFooter(template).text }}
            </p>
          </div>
          <!-- Botoes estilo WhatsApp -->
          <div
            v-if="getTemplateButtons(template)"
            class="ltr:ml-auto rtl:mr-auto max-w-[88%] mt-0.5 space-y-0.5"
          >
            <div
              v-for="button in getTemplateButtons(template).buttons"
              :key="button.text"
              class="flex gap-1.5 justify-center items-center py-1.5 text-sm rounded bg-white dark:bg-[#1f2c34] text-[#00a5f4]"
            >
              <span :class="buttonIcon(button)" class="size-3.5" />
              {{ button.text }}
            </div>
          </div>
        </div>
      </button>
      <div v-if="!filteredTemplateMessages.length" class="py-8 text-center">
        <div v-if="query && whatsAppTemplateMessages.length">
          <p>
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_FOUND') }}
            <strong>{{ query }}</strong>
          </p>
        </div>
        <div v-else-if="!whatsAppTemplateMessages.length" class="space-y-4">
          <p class="text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.PICKER.NO_TEMPLATES_AVAILABLE') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
